/**
 * @description Auth utility helpers for session state checks.
 *
 * Key features:
 * - hasValidStoredSession: checks sessionStorage for a non-expired access token
 *
 * @notes
 * - Used by App.js and ProtectedRoute.js to decide loading UI (skeleton vs minimal loader)
 * - public/index.html first-paint script must use the same rule (token + expiry + 30s buffer)
 * - Does NOT check if the token is cryptographically valid — only checks expiry
 * - Full token validation happens in AuthContext.initializeAuth()
 */

// Buffer before expiry — if the token expires within 30 seconds, treat it as expired.
// A near-expiry token will fail the /me call anyway, so showing the skeleton just
// to immediately flip to a redirect is a worse UX than showing the minimal loader.
const SESSION_VALIDITY_BUFFER_MS = 30 * 1000;

/**
 * Check if sessionStorage contains a non-expired access token.
 *
 * Returns true only if both `access_token` and `token_expires_at` exist
 * and the current time is before the expiry (minus a 30-second buffer).
 * Used to decide whether to show a dashboard skeleton (valid session likely)
 * or a minimal loader (expired session — auth will clear and redirect to /sign-in).
 *
 * @returns {boolean} True if a non-expired token exists in sessionStorage
 */
export const hasValidStoredSession = () => {
  const token = sessionStorage.getItem('access_token');
  const expiresAt = sessionStorage.getItem('token_expires_at');

  if (!token || !expiresAt) return false;

  return Date.now() < (parseInt(expiresAt, 10) - SESSION_VALIDITY_BUFFER_MS);
};
